import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { STORAGE } from '../constants/storageKeys';
import { usePersistentState } from '../hooks/usePersistentState';
import { createRngFromSeed, randomSeed } from '../utils/rng';
import { rosterStringsFromSessionPlayers } from '../utils/tableSession';
import { copyText, buildFirstPlayerPosterPng } from '../utils/share';
import './FirstPlayer.css';

const TITLE_DROPS = [
  'First Explorer',
  'Round Zero Monarch',
  'Chaos Ambassador',
  'Snack Deputy',
  'Speedrun Certified Starter',
  'Chosen by the ancient dice gods',
  'Table Captain',
  'Turn Order Tyrant (benevolent)',
  'Opening Act',
  'Dice Whisperer',
  'Chief Meeple Officer',
  'Tonight’s Catalyst',
  'Snack-adjacent Legend',
  'The one who reads the rules',
  'Final Boss of Setup',
];

const defaultFp = {
  players: [],
  result: null,
  titleDrop: null,
  decidedAt: null,
  seed: null,
};

function parseNames(text) {
  return text
    .split(/[\n,]+/)
    .map((s) => s.trim())
    .filter(Boolean);
}

function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const fn = () => setReduced(mq.matches);
    fn();
    mq.addEventListener('change', fn);
    return () => mq.removeEventListener('change', fn);
  }, []);
  return reduced;
}

export default function FirstPlayer() {
  const [fp, setFp] = usePersistentState(STORAGE.firstPlayer, defaultFp);
  const [settings] = usePersistentState(STORAGE.settings, { reduceMotion: false, soundOn: false, hapticsOn: false });
  const prefersReduced = usePrefersReducedMotion();
  const reduceMotion = settings.reduceMotion || prefersReduced;

  const [text, setText] = useState(() => fp.players.join('\n'));
  const [phase, setPhase] = useState(fp.result ? 'result' : 'setup');
  const [localResult, setLocalResult] = useState(fp.result);
  const [localTitle, setLocalTitle] = useState(fp.titleDrop);
  useEffect(() => {
    setText(fp.players.join('\n'));
  }, [fp.players]);

  const applyKitSessionToSetup = useCallback(() => {
    try {
      const raw = localStorage.getItem(STORAGE.session);
      if (!raw) return;
      const data = JSON.parse(raw);
      const names = rosterStringsFromSessionPlayers(data.players);
      if (names.length === 0) return;
      setText(names.join('\n'));
      setFp((p) => ({ ...p, players: names }));
    } catch {
      /* ignore */
    }
  }, [setFp]);

  useEffect(() => {
    if (phase !== 'setup') return undefined;
    applyKitSessionToSetup();
    const onPersist = (e) => {
      if (e.detail?.key === STORAGE.session) applyKitSessionToSetup();
    };
    const onStorage = (e) => {
      if (e.key === STORAGE.session) applyKitSessionToSetup();
    };
    window.addEventListener('mt-persist', onPersist);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener('mt-persist', onPersist);
      window.removeEventListener('storage', onStorage);
    };
  }, [phase, applyKitSessionToSetup]);

  const importFromScorecard = useCallback(() => {
    try {
      const raw = localStorage.getItem(STORAGE.gamePoints);
      if (!raw) return;
      const data = JSON.parse(raw);
      const names = (data.playerNames || []).filter((n) => n && String(n).trim());
      if (names.length) {
        setText(names.join('\n'));
        setFp((p) => ({ ...p, players: names.map((n) => n.trim()) }));
      }
    } catch {
      /* ignore */
    }
  }, [setFp]);

  const importFromKitSession = useCallback(() => {
    applyKitSessionToSetup();
  }, [applyKitSessionToSetup]);

  const reveal = useCallback(() => {
    const players = parseNames(text);
    if (players.length < 1) {
      window.alert('Add at least one name (or use seat mode).');
      return;
    }
    const seed = randomSeed();
    const rng = createRngFromSeed(seed);
    const pick = players[rng.nextInt(0, players.length - 1)];
    const title = TITLE_DROPS[rng.nextInt(0, TITLE_DROPS.length - 1)];

    const apply = () => {
      setLocalResult(pick);
      setLocalTitle(title);
      setPhase('result');
      setFp({
        players,
        result: pick,
        titleDrop: title,
        decidedAt: Date.now(),
        seed,
      });
    };

    setFp((p) => ({ ...p, players }));
    if (reduceMotion) {
      apply();
      return;
    }
    setPhase('suspense');
    const ms = 350 + Math.floor(Math.random() * 450);
    window.setTimeout(apply, ms);
  }, [text, setFp, reduceMotion]);

  const again = useCallback(() => {
    const players = fp.players.length ? fp.players : parseNames(text);
    if (players.length < 1) return;
    const seed = randomSeed();
    const rng = createRngFromSeed(seed);
    const pick = players[rng.nextInt(0, players.length - 1)];
    const title = TITLE_DROPS[rng.nextInt(0, TITLE_DROPS.length - 1)];
    setLocalResult(pick);
    setLocalTitle(title);
    setFp({
      players,
      result: pick,
      titleDrop: title,
      decidedAt: Date.now(),
      seed,
    });
  }, [fp.players, text, setFp]);

  useEffect(() => {
    if (phase !== 'result' || !localResult) return undefined;
    let last = 0;
    const onDev = (e) => {
      const acc = e.accelerationIncludingGravity;
      if (!acc) return;
      const mag = Math.abs(acc.x || 0) + Math.abs(acc.y || 0) + Math.abs(acc.z || 0);
      const now = Date.now();
      if (mag > 18 && now - last > 1200) {
        last = now;
        again();
      }
    };
    window.addEventListener('devicemotion', onDev, true);
    return () => window.removeEventListener('devicemotion', onDev, true);
  }, [phase, localResult, again]);

  const seats = (n) => {
    const players = Array.from({ length: n }, (_, i) => `Seat ${i + 1}`);
    setText(players.join('\n'));
    setFp((p) => ({ ...p, players }));
  };

  const shareCopy = () => {
    if (!localResult) return;
    copyText(`First player: ${localResult} — chosen by MeepleTab`).then((ok) => {
      window.alert(ok ? 'Copied!' : 'Copy failed');
    });
  };

  const sharePng = async () => {
    if (!localResult) return;
    const url = await buildFirstPlayerPosterPng({
      name: localResult,
      title: localTitle || '',
      date: new Date().toLocaleString(),
    });
    if (!url) {
      window.alert('Could not build image');
      return;
    }
    const a = document.createElement('a');
    a.href = url;
    a.download = `meepletab-first-player-${localResult.replace(/\s+/g, '-')}.png`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="screen">
      <Link to="/" className="kit-back-link">
        ← Game Night Kit
      </Link>
      <h1 className="screen-title">First player</h1>

      {phase === 'setup' && (
        <div className="screen-panel">
          <label htmlFor="fp-names" className="screen-title" style={{ fontSize: '1.1rem', display: 'block' }}>
            Who&apos;s playing?
          </label>
          <textarea
            id="fp-names"
            className="fp-names"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="One name per line, or comma-separated"
          />
          <div className="fp-seats">
            <span style={{ alignSelf: 'center', color: 'var(--text-secondary)', fontWeight: 700 }}>Seats:</span>
            {[3, 4, 5, 6].map((n) => (
              <button type="button" key={n} onClick={() => seats(n)}>
                {n} seats
              </button>
            ))}
          </div>
          <button type="button" className="fp-primary" onClick={reveal}>
            Reveal first player
          </button>
          <button type="button" className="fp-again" style={{ marginTop: 10, width: '100%' }} onClick={importFromKitSession}>
            Import Kit roster (dashboard)
          </button>
          <button type="button" className="fp-again" style={{ marginTop: 10, width: '100%' }} onClick={importFromScorecard}>
            Import names from Scorecard
          </button>
        </div>
      )}

      {phase === 'suspense' && (
        <div className={`screen-panel fp-suspense ${reduceMotion ? '' : 'spinning'}`} aria-live="polite">
          Spinning…
        </div>
      )}

      {phase === 'result' && localResult && (
        <div className="screen-panel">
          <div className="screen-title" style={{ fontSize: '1.2rem', textAlign: 'center' }}>
            First player
          </div>
          <div className="fp-result-name" aria-live="assertive">
            {localResult}
          </div>
          {localTitle && <div className="fp-title-drop">{localTitle}</div>}
          <div className="fp-actions">
            <button type="button" className="fp-share" onClick={shareCopy}>
              Share (copy)
            </button>
            <button type="button" className="fp-share" style={{ background: 'rgba(0,242,254,0.25)', border: '2px solid var(--accent-cyan)' }} onClick={sharePng}>
              Poster PNG
            </button>
            <button type="button" className="fp-again" onClick={again}>
              Again
            </button>
          </div>
          <button
            type="button"
            className="fp-again"
            style={{ width: '100%', marginTop: 12 }}
            onClick={() => {
              setPhase('setup');
              setLocalResult(null);
              setLocalTitle(null);
              setFp({ ...defaultFp, players: fp.players });
            }}
          >
            Edit names
          </button>
        </div>
      )}
    </div>
  );
}
